import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { client } from './api';

const STORAGE_KEY = 'rentalverse_jewelry_auth';

const SESSION_EXPIRED_EVENT = `${STORAGE_KEY}:expired`;

// Decoding is only for scheduling logout; the server verifies token signatures.
function expiresAt(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : 0;
  } catch { return 0; }
}

function expireSession(token) {
  let current;
  try { current = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { current = null; }
  if (current?.token && current.token !== token) return;
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

function readStoredAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : null;
    if (stored && expiresAt(stored.token) <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

function writeStoredAuth(auth) {
  if (auth) localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
  else localStorage.removeItem(STORAGE_KEY);
}

// Attaches the current token (if any) to every request. Registered once at module load, so
// import this module near the top of the app (see App.js) before any authenticated calls fire.
client.interceptors.request.use((config) => {
  const stored = readStoredAuth();
  if (stored?.token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${stored.token}`;
  }
  return config;
});

client.interceptors.response.use((response) => response, (error) => {
  // Password/login failures also use 401, so only invalidate rejected session tokens.
  if (error.response?.status === 401 &&
      error.response?.data?.msg === 'Invalid or expired token') {
    const authorization = error.config?.headers?.Authorization;
    if (authorization?.startsWith('Bearer ')) expireSession(authorization.slice(7));
  }
  return Promise.reject(error);
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(readStoredAuth);

  useEffect(() => {
    writeStoredAuth(auth);
  }, [auth]);

  useEffect(() => {
    const sync = () => setAuth(readStoredAuth());
    window.addEventListener(SESSION_EXPIRED_EVENT, sync);
    const storageChanged = (event) => {
      if (event.key === STORAGE_KEY || event.key === null) sync();
    };
    window.addEventListener('storage', storageChanged);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, sync);
      window.removeEventListener('storage', storageChanged);
    };
  }, []);

  useEffect(() => {
    if (!auth?.token) return undefined;
    let timer;
    const check = () => {
      clearTimeout(timer);
      const remaining = expiresAt(auth.token) - Date.now();
      if (remaining <= 0) {
        expireSession(auth.token);
      } else {
        // Browser timers overflow beyond ~24.8 days; schedule longer sessions in chunks.
        timer = setTimeout(check, Math.min(remaining, 2147483647));
      }
    };
    check();
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [auth?.token]);

  const login = useCallback(async (email, password) => {
    const res = await client.post('/api/auth/login', { email, password });
    writeStoredAuth(res.data);
    setAuth(res.data);
    return res.data.user;
  }, []);

  const signup = useCallback(async (payload) => {
    const res = await client.post('/api/auth/signup', payload);
    writeStoredAuth(res.data);
    setAuth(res.data);
    return res.data.user;
  }, []);

  const logout = useCallback(() => {
    writeStoredAuth(null);
    setAuth(null);
  }, []);

  // Swap the signed-in user's password — voluntary, or the forced first-login change an
  // admin/owner-onboarded account must do before the rest of the app unlocks.
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res = await client.post('/api/auth/change-password', { currentPassword, newPassword });
    writeStoredAuth(res.data);
    setAuth(res.data);
    return res.data.user;
  }, []);

  const value = useMemo(
    () => ({
      user: auth?.user || null,
      token: auth?.token || null,
      isAuthenticated: !!auth?.token,
      mustChangePassword: !!auth?.user?.mustChangePassword,
      login,
      signup,
      logout,
      changePassword
    }),
    [auth, login, signup, logout, changePassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
